import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatDropdown } from "@cat-digital-blocks/react/bedrock/components/Dropdown";
import { CatInputField } from "@cat-digital-blocks/react/bedrock/components/InputField";
import { CatTextareaField } from "@cat-digital-blocks/react/bedrock/components/TextareaField";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatList } from "@cat-digital-blocks/react/bedrock/components/List";
import { CatListItem } from "@cat-digital-blocks/react/bedrock/components/ListItem";
import { CatToggle } from "@cat-digital-blocks/react/bedrock/components/Toggle";
import { CatCheckboxField } from "@cat-digital-blocks/react/bedrock/components/CheckboxField";
import {
  useGetAttributeMappingsQuery,
  useCreateRuleMappingMutation,
  useGetDealersQuery,
} from "@services/dcmApi";
import styles from "./RuleRegistration.module.scss";

type PalType = "LEADS" | "OPPORTUNITY" | "TASKS" | "CASE";

interface CreateRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  palType: PalType;
  userName?: string;
}

const CreateRuleModal: React.FC<CreateRuleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  palType,
  userName,
}) => {
  const { t } = useTranslation();

  // Form state
  const [selectedAttributeId, setSelectedAttributeId] = useState<number | null>(
    null,
  );
  const [ruleDescription, setRuleDescription] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [ruleOperator, setRuleOperator] = useState("");
  const [enableApplicableDealers, setEnableApplicableDealers] = useState(false);
  const [selectedDealers, setSelectedDealers] = useState<string[]>([]);
  const [validFrom, setValidFrom] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [validUntil, setValidUntil] = useState(() => {
    const sixMonthsLater = new Date();
    sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
    return sixMonthsLater.toISOString().split("T")[0];
  });

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch attributes for the PAL type
  const { data: attributes = [] } = useGetAttributeMappingsQuery({
    palType,
  });

  // Fetch dealers for multi-select
  const { data: dealers = [] } = useGetDealersQuery();

  const [createRule] = useCreateRuleMappingMutation();

  // Get the selected attribute
  const selectedAttribute = useMemo(() => {
    return attributes.find((attr) => attr.id === selectedAttributeId);
  }, [attributes, selectedAttributeId]);

  // Available operators from selected attribute
  const availableOperators = useMemo(() => {
    return selectedAttribute?.allowedOperators || [];
  }, [selectedAttribute]);

  // Attribute dropdown options
  const attributeOptions = useMemo(() => {
    return attributes.map((attr) => ({
      label: `${attr.mappingColumn} (${attr.sourceName})`,
      value: String(attr.id),
    }));
  }, [attributes]);

  // Operator dropdown options
  const operatorOptions = useMemo(() => {
    return availableOperators.map((op) => ({
      label: op,
      value: op,
    }));
  }, [availableOperators]);

  // Reset operator when attribute changes
  useEffect(() => {
    if (selectedAttribute && operatorOptions.length > 0 && !ruleOperator) {
      setRuleOperator(operatorOptions[0].value);
    }
  }, [selectedAttribute, operatorOptions, ruleOperator]);

  const handleAttributeChange = useCallback((event: CustomEvent) => {
    const value = event.detail?.value;
    if (value) {
      setSelectedAttributeId(Number(value));
      setRuleOperator(""); // Reset operator when attribute changes
    }
  }, []);

  const handleRuleDescriptionChange = useCallback((event: CustomEvent) => {
    setRuleDescription(event.detail?.value || "");
  }, []);

  const handleDefaultValueChange = useCallback((event: CustomEvent) => {
    setDefaultValue(event.detail?.value || "");
  }, []);

  const handleOperatorChange = useCallback((event: CustomEvent) => {
    setRuleOperator(event.detail?.value || "");
  }, []);

  const handleToggleApplicableDealers = useCallback((event: CustomEvent) => {
    const checked = event.detail?.checked ?? false;
    setEnableApplicableDealers(checked);
    if (!checked) {
      setSelectedDealers([]);
    }
  }, []);

  const handleDealerSelection = useCallback((event: CustomEvent) => {
    const selectedItems = event.detail?.values;
    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      const newValues = selectedItems.map((item: string | { value: string }) =>
        typeof item === "string" ? item : item.value,
      );
      setSelectedDealers(newValues);
    } else {
      setSelectedDealers([]);
    }
  }, []);

  const handleValidFromChange = useCallback((event: CustomEvent) => {
    setValidFrom(event.detail?.value || "");
  }, []);

  const handleValidUntilChange = useCallback((event: CustomEvent) => {
    setValidUntil(event.detail?.value || "");
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);

    // Validation
    if (!selectedAttributeId) {
      setError(t("admin.ruleRegistration.validation.attributeRequired"));
      return;
    }

    if (!ruleOperator) {
      setError(t("admin.ruleRegistration.validation.operatorRequired"));
      return;
    }

    if (!validFrom) {
      setError(t("admin.ruleRegistration.validation.validFromRequired"));
      return;
    }

    if (!validUntil) {
      setError(t("admin.ruleRegistration.validation.validUntilRequired"));
      return;
    }

    // Default rule language to en-US
    const ruleLanguage = "en-US";

    // Use selected dealers array if enabled
    const dealersArray = enableApplicableDealers && selectedDealers.length > 0
      ? selectedDealers
      : undefined;

    setIsLoading(true);

    try {
      await createRule({
        body: {
          attributeId: selectedAttributeId,
          ruleLanguage,
          ruleDescription: ruleDescription || undefined,
          defaultValue: defaultValue || undefined,
          ruleOperator,
          applicableDealers: dealersArray,
          validFrom,
          validUntil,
        },
        userName,
      }).unwrap();

      onSuccess();
    } catch (err: unknown) {
      if (
        typeof err === "object" &&
        err !== null &&
        "status" in err &&
        (err as { status: number }).status === 409
      ) {
        setError(t("admin.ruleRegistration.error.duplicate"));
      } else {
        setError(t("admin.ruleRegistration.error.createFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedAttributeId,
    selectedAttribute,
    ruleDescription,
    defaultValue,
    ruleOperator,
    enableApplicableDealers,
    selectedDealers,
    validFrom,
    validUntil,
    createRule,
    userName,
    onSuccess,
    t,
  ]);

  const handleCancel = useCallback(() => {
    setSelectedAttributeId(null);
    setRuleDescription("");
    setDefaultValue("");
    setRuleOperator("");
    setEnableApplicableDealers(false);
    setSelectedDealers([]);
    setError(null);
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <CatHeading variant="title-sm">
            {t("admin.ruleRegistration.createModal.title")}
          </CatHeading>
        </div>

        <div className={styles.modalBody}>
          {error && (
            <CatInlineNotification
              variant="error"
              className={styles.errorMessage}
            >
              {error}
            </CatInlineNotification>
          )}

          {/* Available Attributes Dropdown */}
          <div className={styles.formGroup}>
            <label>{t("admin.ruleRegistration.createModal.attribute")} *</label>
            <CatDropdown
              label=""
              size="sm"
              placeholder={t(
                "admin.ruleRegistration.createModal.attributePlaceholder",
              )}
              value={selectedAttributeId ? String(selectedAttributeId) : ""}
              onBlSelect={handleAttributeChange}
              disabled={isLoading}
            >
              <CatList spacing="padded">
                {attributeOptions.map((opt) => (
                  <CatListItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </CatListItem>
                ))}
              </CatList>
            </CatDropdown>
          </div>

          {/* Rule Description */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.ruleRegistration.createModal.ruleDescription")}
            </label>
            <div className={styles.noLabelField}>
              <CatTextareaField
                label=" "
                value={ruleDescription}
                onBlChange={handleRuleDescriptionChange}
                placeholder={t(
                  "admin.ruleRegistration.createModal.ruleDescriptionPlaceholder",
                )}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Default Value */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.ruleRegistration.createModal.defaultValue")}
            </label>
            <CatInputField
              label=""
              value={defaultValue}
              onBlChange={handleDefaultValueChange}
              placeholder={t(
                "admin.ruleRegistration.createModal.defaultValuePlaceholder",
              )}
              disabled={isLoading}
            />
          </div>

          {/* Rule Operator */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.ruleRegistration.createModal.ruleOperator")} *
            </label>
            <CatDropdown
              label=""
              size="sm"
              placeholder={
                selectedAttributeId
                  ? t("admin.ruleRegistration.createModal.operatorPlaceholder")
                  : t("admin.ruleRegistration.createModal.selectAttributeFirst")
              }
              value={ruleOperator}
              onBlSelect={handleOperatorChange}
              disabled={isLoading || !selectedAttributeId}
            >
              <CatList spacing="padded">
                {operatorOptions.map((opt) => (
                  <CatListItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </CatListItem>
                ))}
              </CatList>
            </CatDropdown>
          </div>

          {/* Applicable Dealers Toggle and Multi-Select */}
          <div className={styles.formGroup}>
            <label>
              {t("admin.ruleRegistration.createModal.applicableDealers")}
            </label>
            <div className={styles.dealerToggleRow}>
              <CatToggle
                label=""
                hideLabel
                checked={enableApplicableDealers}
                onBlToggleChange={handleToggleApplicableDealers}
                disabled={isLoading}
              >
                <span slot="checked-label">{t("common.enabled")}</span>
                <span slot="unchecked-label">{t("common.disabled")}</span>
              </CatToggle>
            </div>
            {enableApplicableDealers && (
              <div className={styles.dealerDropdown}>
                <CatDropdown
                  label=""
                  size="sm"
                  placeholder={t(
                    "admin.ruleRegistration.createModal.selectDealersPlaceholder",
                  )}
                  isMultiSelect={true}
                  disabled={isLoading}
                  onBlDropdownMultiSelect={handleDealerSelection}
                >
                  {dealers.map((dealer) => (
                    <CatCheckboxField
                      key={dealer.dealerCode}
                      value={dealer.dealerCode}
                      checked={selectedDealers.includes(dealer.dealerCode)}
                      hasHover
                    >
                      {dealer.displayName}
                    </CatCheckboxField>
                  ))}
                </CatDropdown>
              </div>
            )}
          </div>

          {/* Valid From & Valid Until */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>
                {t("admin.ruleRegistration.createModal.validFrom")} *
              </label>
              <CatInputField
                label=""
                value={validFrom}
                onBlChange={handleValidFromChange}
                placeholder="YYYY-MM-DD"
                disabled={isLoading}
              />
            </div>
            <div className={styles.formGroup}>
              <label>
                {t("admin.ruleRegistration.createModal.validUntil")} *
              </label>
              <CatInputField
                label=""
                value={validUntil}
                onBlChange={handleValidUntilChange}
                placeholder="YYYY-MM-DD"
                disabled={isLoading}
              />
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <CatButton
            variant="contrast"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </CatButton>
          <CatButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {t("common.save")}
          </CatButton>
        </div>
      </div>
    </div>
  );
};

export default CreateRuleModal;
